import { ai } from '../firebase/config';
import { getGenerativeModel } from 'firebase/ai';
import { getAuth } from 'firebase/auth';
import { getDoc, doc, getFirestore } from 'firebase/firestore';

// Define the configuration interface
interface AIFeatureConfig {
    research: string;
    chat: string;
    libraryManagement: string;
    examGeneration: string;
    autoRemarks: string;
    navigationSearch: string;
    admissionPattern: string;
    audioAssistant: string;
    // Independent (teach.sonoma.school) platform overrides
    independent_research?: string;
    independent_chat?: string;
    independent_libraryManagement?: string;
    independent_examGeneration?: string;
    independent_autoRemarks?: string;
    independent_navigationSearch?: string;
    independent_admissionPattern?: string;
    independent_audioAssistant?: string;
    independent_visionAssistant?: string; // Multimodal vision for image analysis
    fallback?: string;
    independent_fallback?: string;
    isPaused?: boolean;
    pausedSchools?: string[];
}

// Sanitize model names to prevent 404 errors with retired or preview models
const sanitizeModelName = (modelName: string): string => {
    if (!modelName) throw new Error("No model name provided.");
    return modelName;
};

// Helper to get the correct model for a feature with robust fallbacks
const getModelForFeature = async (feature: keyof AIFeatureConfig, contextId?: string): Promise<string> => {
    const db = getFirestore();
    const configRef = doc(db, 'systemConfig', 'aiModels');
    const configSnap = await getDoc(configRef);

    if (!configSnap.exists()) {
        throw new Error("AI configuration not found. Please save your configuration in the AI Control panel.");
    }

    const config = configSnap.data() as AIFeatureConfig;

    // 1. Check Global Pause
    if (config.isPaused) {
        throw new Error("AI services are currently paused globally.");
    }

    // 2. Check Context Pause (School or Workspace)
    if (contextId && config.pausedSchools && config.pausedSchools.includes(contextId)) {
        throw new Error("AI services are paused for this workspace/school.");
    }

    // 3. Try to get the configured model for this feature
    let modelValue = config[feature];
    if (typeof modelValue === 'string' && modelValue.trim() !== '') {
        console.warn(`[AI Config Pinpoint] Retrieved model '${modelValue}' directly from Firestore for feature '${feature}'.`);
        return sanitizeModelName(modelValue);
    }

    // 4. Fallback chain for independent features
    if (feature.startsWith('independent_')) {
        const mainFeatureKey = feature.replace('independent_', '') as keyof AIFeatureConfig;
        const mainModelValue = config[mainFeatureKey];
        if (typeof mainModelValue === 'string' && mainModelValue.trim() !== '') {
            console.warn(`[AI Config Pinpoint] Independent feature '${feature}' was not set. Fell back to School feature '${mainFeatureKey}' which is '${mainModelValue}' in Firestore.`);
            return sanitizeModelName(mainModelValue);
        }
    }

    throw new Error(`Model not configured for feature: ${feature}. Please update AI Control.`);
};

// Helper to get the fallback model for a feature
const getFallbackModel = async (feature: keyof AIFeatureConfig): Promise<string> => {
    const db = getFirestore();
    const configRef = doc(db, 'systemConfig', 'aiModels');
    const configSnap = await getDoc(configRef);

    if (!configSnap.exists()) {
        throw new Error("AI configuration not found. Please save your configuration in the AI Control panel.");
    }

    const config = configSnap.data() as AIFeatureConfig;
    
    if (feature.startsWith('independent_')) {
        if (config.independent_fallback && config.independent_fallback.trim() !== '') {
            return sanitizeModelName(config.independent_fallback);
        }
    }
    
    if (config.fallback && config.fallback.trim() !== '') {
        return sanitizeModelName(config.fallback);
    }

    throw new Error("No fallback model configured in AI Control.");
};

// Log AI Usage (Simplified)
const logAIUsage = async (tokens: number, feature: string, contextId?: string) => {
    // Implementation of logging would go here
    // For now we just log to console to avoid circular deps or complexity
    console.log(`[AI Usage] ${tokens} tokens for ${feature} in ${contextId}`);
};

// Blob to Base64
async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

/**
 * Generates content using Gemini — supports text, audio, and image inputs.
 * @param prompt     The text prompt / instruction
 * @param mediaBlob  An audio or image Blob (optional). The mimeType decides the feature key.
 * @param contextId  The user UID or school ID (for logging/config lookup)
 * @param feature    Explicit feature key override (default: auto-detected from blob type)
 */
export const geminiMultimodalGenerate = async (
    prompt: string,
    mediaBlob: Blob | null,
    contextId?: string,
    feature?: keyof AIFeatureConfig
) => {
    try {
        // Detect media type and resolve feature key
        const isImage = mediaBlob?.type.startsWith('image/');
        const isAudio = mediaBlob?.type.startsWith('audio/');

        // Auto-pick feature key if not explicitly supplied
        const resolvedFeature: keyof AIFeatureConfig = feature
            ? feature
            : isImage
                ? 'independent_visionAssistant'
                : isAudio
                    ? 'independent_audioAssistant'
                    : 'independent_chat';

        console.log(`[GeminiMultimodal] Starting. Feature: ${resolvedFeature}, Context: ${contextId}, Media: ${mediaBlob?.type || 'none'}`);

        // 1. Get Configured Model
        const modelName = await getModelForFeature(resolvedFeature, contextId);
        const platformSection = resolvedFeature.startsWith('independent_') ? 'Independent' : 'School';
        console.warn(`[GeminiMultimodal Diagnostics]`);
        console.warn(`-> Section: ${platformSection} Platform`);
        console.warn(`-> Feature: ${resolvedFeature}`);
        console.warn(`-> Model: ${modelName}`);
        console.warn(`-> Source: Firebase 'systemConfig/aiModels' document`);
        console.warn(`--------------------------------------------------`);

        // 2. Initialize Model
        const model = getGenerativeModel(ai, { model: modelName });

        // 3. Build Parts
        const parts: any[] = [];

        if (mediaBlob && isImage) {
            // ── Image path ──
            console.log(`[GeminiMultimodal] Processing image: ${mediaBlob.type}, size: ${mediaBlob.size}`);
            const base64Image = await blobToBase64(mediaBlob);
            // Image part FIRST so the model sees it before the instruction text
            parts.push({
                inlineData: {
                    mimeType: mediaBlob.type,
                    data: base64Image
                }
            });
            // Vision-specific instruction prepended to the user prompt
            const visionInstruction = `You are an expert educational AI assistant helping a teacher.
An image has been uploaded. It may be a photo of a whiteboard, textbook page, worksheet, or any written content.

Your task:
1. Read and extract all text/questions visible in the image.
2. Identify each question or problem.
3. For each problem, provide:
   - The extracted question text
   - Step-by-step working / solution
   - A clear final answer
4. If there are multiple questions, label them clearly (Q1, Q2, etc.).
5. If the image content is unclear, describe what you can see and attempt to help anyway.

Additional teacher instruction: ${prompt || 'Analyse this image and solve any questions shown.'}\n`;
            parts.push({ text: visionInstruction });

        } else if (mediaBlob && isAudio) {
            // ── Audio path ──
            console.log(`[GeminiMultimodal] Processing audio: ${mediaBlob.type}, size: ${mediaBlob.size}`);
            const base64Audio = await blobToBase64(mediaBlob);
            parts.push({
                inlineData: {
                    mimeType: mediaBlob.type || 'audio/webm',
                    data: base64Audio
                }
            });
            parts.push({ text: 'Please listen to this audio query and respond. ' + (prompt || '') });

        } else {
            // ── Text-only path ──
            if (prompt) parts.push({ text: prompt });
        }

        // 4. Generate
        console.log(`[GeminiMultimodal] Sending ${parts.length} part(s) to Gemini...`);
        let result: any;
        try {
            result = await model.generateContent(parts);
        } catch (err: any) {
            console.warn(`[GeminiMultimodal] First attempt failed with ${modelName}:`, err);
            // Retry with configured fallback model
            let fallbackModelName;
            try {
                fallbackModelName = await getFallbackModel(resolvedFeature);
            } catch (fallbackConfigErr) {
                console.warn(`[GeminiMultimodal] Fallback config error:`, fallbackConfigErr);
                throw err; // throw original error if no fallback configured
            }
            
            const fallbackModels = [fallbackModelName];
            
            let success = false;
            for (const fallback of fallbackModels) {
                if (fallback === modelName) continue;
                console.log(`[GeminiMultimodal] Retrying with ${fallback}...`);
                try {
                    const fallbackModel = getGenerativeModel(ai, { model: fallback });
                    result = await fallbackModel.generateContent(parts);
                    success = true;
                    break;
                } catch (fallbackErr) {
                    console.warn(`[GeminiMultimodal] Fallback ${fallback} failed:`, fallbackErr);
                }
            }
            if (!success) {
                throw err;
            }
        }
        const responseText = result.response.text();
        console.log(`[GeminiMultimodal] Response received (${responseText.length} chars)`);

        // 5. Log Usage
        const approximateTokens = Math.ceil((prompt.length / 4) + (mediaBlob ? (isImage ? 2000 : 1000) : 0));
        logAIUsage(approximateTokens, String(resolvedFeature), contextId);

        return responseText;

    } catch (error) {
        console.error("Gemini Multimodal Error:", error);
        throw error;
    }
};
